import { createContext, useContext, useState, ReactNode, useEffect, useRef, useCallback } from "react";
import type { CartItem, SelectedModifier, UserData, ApplicableDiscount, SechUser, TaxCalculationResult } from "@/types/pos";
import type { SelectablePaymentMethod } from "@/types/payment-methods";
import { useToast } from "@/hooks/use-toast";
import { useSechPrice } from "@/hooks/pos/use-sech-price";
import { apiFetch } from "@/utils";
import PosConfirmModal from "@/components/pos/confirmation-modal";

function areModifiersEqual(
	mods1: SelectedModifier[] | null | undefined,
	mods2: SelectedModifier[] | null | undefined
): boolean {
	const cartMods = mods1 || [];
	const itemMods = mods2 || [];
	if (cartMods.length !== itemMods.length) return false;
	const sortedCartMods = [...cartMods].sort((a, b) => a.modifierId - b.modifierId);
	const sortedItemMods = [...itemMods].sort((a, b) => a.modifierId - b.modifierId);
	return sortedCartMods.every((mod, idx) => mod.modifierId === sortedItemMods[idx].modifierId);
}

interface CartContextType {
	cartItems: CartItem[];
	addToCart: (item: CartItem) => void;
	removeFromCart: (itemId: string) => void;
	updateQuantity: (itemId: string, quantity: number) => void;
	clearCart: () => void;
	getCartTotal: () => number;
	getCartItemCount: () => number;
	sechUser: SechUser | null;
	requestPayment: (orderId?: string, userData?: UserData, notes?: string, paymentMethod?: SelectablePaymentMethod) => void;
	isPaymentProcessing: boolean;
	taxCalculation: TaxCalculationResult | null;
	isCalculatingTaxes: boolean;
	appliedDiscount: ApplicableDiscount | null;
	promoCode: string;
	setPromoCode: (code: string) => void;
	applyPromoCode: (paymentMethod?: string) => Promise<void>;
	removeDiscount: () => void;
	isValidatingPromo: boolean;
	promoError: string | null;
	getDiscountedTotal: () => number;
	refreshDiscount: (paymentMethod?: string) => void;
}

interface CartProviderProps {
	children: ReactNode;
	sechUser?: SechUser | null;
	executePayment: (paymentData: any) => Promise<{ success: boolean; txHash?: string; orderId?: string; error?: string }>;
	onPaymentSuccess?: (order: any) => void;
	onCartChange?: (hasItems: boolean) => void;
	location?: { id: number; name?: string; details?: string } | null;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function PosCartProvider({
	children,
	sechUser = null,
	executePayment,
	onPaymentSuccess,
	onCartChange,
	location
}: CartProviderProps) {
	const [cartItems, setCartItems] = useState<CartItem[]>([]);
	const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
	const [taxCalculation, setTaxCalculation] = useState<TaxCalculationResult | null>(null);
	const [isCalculatingTaxes, setIsCalculatingTaxes] = useState(false);
	const [cartLocation, setCartLocation] = useState<string | null>(null);
	const [pending, setPending] = useState<CartItem | null>(null);

	const [appliedDiscount, setAppliedDiscount] = useState<ApplicableDiscount | null>(null);
	const [promoCode, setPromoCode] = useState("");
	const [isValidatingPromo, setIsValidatingPromo] = useState(false);
	const [promoError, setPromoError] = useState<string | null>(null);
	const [manualPromoApplied, setManualPromoApplied] = useState(false);
	const [autoApplyDismissed, setAutoApplyDismissed] = useState(false);

	const { toast } = useToast();
	const { sechPrice } = useSechPrice();

	// Notify parent about cart state changes
	useEffect(() => {
		onCartChange?.(cartItems.length > 0);
	}, [cartItems.length, onCartChange]);

	const userRef = useRef<UserData | null>(null);
	const notesRef = useRef<string | null>(null);



	const addToCart = (item: CartItem) => {
		if (cartLocation && cartLocation !== item?.itemLocation) {
			setPending(item);
			return;
		}
		if (!cartLocation) {
			setCartLocation(item?.itemLocation);
		}
		setCartItems(prev => {
			const existingItemIndex = prev.findIndex(cartItem => {
				if (item.type === "product" && cartItem.type === "product") {
					if (cartItem.productId !== item.productId) return false;
					return areModifiersEqual(cartItem.selectedModifiers, item.selectedModifiers);
				}
				return false;
			});

			if (existingItemIndex >= 0) {
				const updated = [...prev];
				updated[existingItemIndex] = {
					...updated[existingItemIndex],
					quantity: updated[existingItemIndex].quantity + item.quantity,
				};
				return updated;
			}
			return [...prev, item];
		});
		toast({
			title: "Added to cart",
			description: `${item.product?.name || item.customDrink?.name} added to your cart.`,
		});
	};

	const removeFromCart = (itemId: string) => {
		setCartItems(prev => prev.filter(item => item.id !== itemId));
		toast({
			title: "Removed from cart",
			description: "Item has been removed from your cart.",
		});
	};

	const updateQuantity = (itemId: string, quantity: number) => {
		if (quantity <= 0) {
			removeFromCart(itemId);
			return;
		}
		setCartItems(prev =>
			prev.map(item => item.id === itemId ? { ...item, quantity } : item)
		);
	};

	const clearCart = () => {
		setCartItems([]);
		setTaxCalculation(null);
		setCartLocation(null);
		setAppliedDiscount(null);
		setPromoCode("");
		setPromoError(null);
		setManualPromoApplied(false);
		setAutoApplyDismissed(false);
	};

	const getCartTotal = () => {
		return cartItems.reduce((total, item) => total + (item.unitPriceUsd * item.quantity), 0);
	};

	const getCartTotalWithTaxes = () => {
		return taxCalculation ? taxCalculation.total : getCartTotal();
	};

	const getCartItemCount = () => {
		return cartItems.reduce((count, item) => count + item.quantity, 0);
	};

	const getDiscountedTotal = () => {
		const subtotal = getCartTotal();
		if (appliedDiscount) {
			return Math.max(0, subtotal - appliedDiscount.calculatedAmount);
		}
		return subtotal;
	};

	const calculateDiscount = useCallback(async (items: CartItem[], subtotal: number, manualCode?: string, paymentMethod?: string) => {
		if (items.length === 0 || subtotal <= 0 || !location?.id) {
			setAppliedDiscount(null);
			return;
		}

		try {
			const body: Record<string, any> = {
				items: items.map(item => ({
					productId: item.productId,
					productCategory: item.product?.category,
					productSubcategoryId: item.product?.subcategory?.id,
					locationId: location.id,
					unitPriceUsd: item.unitPriceUsd,
					quantity: item.quantity,
				})),
				locationId: location.id,
				cartSubtotal: subtotal,
				customerId: sechUser?.uid,
				promoCode: manualCode || undefined,
			};
			if (paymentMethod) body.paymentMethod = paymentMethod;

			const response = await apiFetch('/v1/pos/discounts/validate', {
				withCredentials: true,
				method: 'POST',
				body: JSON.stringify(body),
			});

			let result;
			try {
				result = await response.json();
			} catch {
				return;
			}

			if (!response.ok) return;

			if (result.bestDiscount) {
				setAppliedDiscount(result.bestDiscount);
			} else if (!manualCode) {
				setAppliedDiscount(null);
			}
		} catch (error) {
			console.error('Failed to calculate discounts:', error);
		}
	}, [location?.id, sechUser?.uid]);

	const applyPromoCode = async (paymentMethod?: string) => {
		if (!promoCode.trim()) {
			setPromoError("Please enter a promo code");
			return;
		}

		setIsValidatingPromo(true);
		setPromoError(null);

		try {
			const subtotal = getCartTotal();
			const body: Record<string, any> = {
				items: cartItems.map(item => ({
					productId: item.productId,
					productCategory: item.product?.category,
					productSubcategoryId: item.product?.subcategory?.id,
					locationId: location?.id,
					unitPriceUsd: item.unitPriceUsd,
					quantity: item.quantity,
				})),
				locationId: location?.id,
				cartSubtotal: subtotal,
				customerId: sechUser?.uid,
				promoCode: promoCode.trim(),
			};
			if (paymentMethod) body.paymentMethod = paymentMethod;

			const response = await apiFetch('/v1/pos/discounts/validate', {
				withCredentials: true,
				method: 'POST',
				body: JSON.stringify(body),
			});

			let result;
			try {
				result = await response.json();
			} catch {
				setPromoError("Server error - please try again");
				return;
			}

			if (!response.ok || !result.bestDiscount) {
				setPromoError(result.message || "Invalid or expired promo code");
				return;
			}

			const newDiscount = result.bestDiscount;

			if (newDiscount.promoCode !== promoCode.trim().toUpperCase()) {
				setPromoError("Invalid or expired promo code");
				return;
			}

			if (appliedDiscount && appliedDiscount.calculatedAmount >= newDiscount.calculatedAmount) {
				setPromoError(`Your current discount saves you more ($${appliedDiscount.calculatedAmount.toFixed(2)} vs $${newDiscount.calculatedAmount.toFixed(2)})`);
				return;
			}

			setAppliedDiscount(newDiscount);
			setManualPromoApplied(true);
			setAutoApplyDismissed(false);
			setPromoError(null);

			toast({
				title: "Promo code applied!",
				description: `${newDiscount.discountName} - You save $${newDiscount.calculatedAmount.toFixed(2)}`,
			});
		} catch {
			setPromoError("Failed to validate promo code");
		} finally {
			setIsValidatingPromo(false);
		}
	};

	const removeDiscount = () => {
		if (appliedDiscount && appliedDiscount.isAutoApply && !manualPromoApplied) {
			setAutoApplyDismissed(true);
		}
		setAppliedDiscount(null);
		setPromoCode("");
		setPromoError(null);
		setManualPromoApplied(false);
	};

	const refreshDiscount = (paymentMethod?: string) => {
		setAppliedDiscount(null);
		setManualPromoApplied(false);
		setAutoApplyDismissed(false);
		setPromoCode("");
		setPromoError(null);
		// Re-trigger auto-apply with new payment method
		const cartTotal = getCartTotal();
		if (cartItems.length > 0 && cartTotal > 0) {
			calculateDiscount(cartItems, cartTotal, undefined, paymentMethod);
		}
	};

	const addDirect = (item: CartItem) => {
		setCartItems(prev => [...prev, item]);
	};

	// Auto-apply discounts when cart changes
	useEffect(() => {
		const cartTotal = getCartTotal();
		if (cartItems.length > 0 && cartTotal > 0) {
			if (manualPromoApplied) {
				calculateDiscount(cartItems, cartTotal, promoCode.trim());
			} else if (!autoApplyDismissed) {
				calculateDiscount(cartItems, cartTotal);
			}
		} else if (cartItems.length === 0) {
			setAppliedDiscount(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cartItems, calculateDiscount, manualPromoApplied, autoApplyDismissed]);

	// Calculate taxes when cart or discount changes
	useEffect(() => {
		const discountedTotal = getDiscountedTotal();

		if (discountedTotal > 0) {
			const calculateTaxes = async () => {
				setIsCalculatingTaxes(true);
				try {
					const response = await apiFetch('/v1/taxes/calculate', {
						method: 'POST',
						body: JSON.stringify({
							amount: discountedTotal,
							transactionType: 'order'
						})
					});

					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}

					const result = await response.json();
					if (result.success) {
						setTaxCalculation(result.data);
					} else {
						throw new Error(result.message || 'Tax calculation failed');
					}
				} catch (error) {
					console.error('Failed to calculate taxes:', error);
					setTaxCalculation({
						subtotal: discountedTotal,
						fees: [],
						total: discountedTotal
					});
				} finally {
					setIsCalculatingTaxes(false);
				}
			};
			calculateTaxes();
		} else {
			setTaxCalculation(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cartItems, appliedDiscount]);

	const saveOrderToDatabase = async (
		orderData: any,
		items: CartItem[],
		taxCalc: TaxCalculationResult | null,
		discount: ApplicableDiscount | null,
		paymentMethod?: SelectablePaymentMethod
	) => {
		try {
			if (!sechPrice) {
				throw new Error('No SECH price available for order recording');
			}

			const originalSubtotal = items.reduce((sum, item) => sum + (item.unitPriceUsd * item.quantity), 0);
			const subtotal = originalSubtotal.toFixed(2);
			let taxAmount = null;
			let taxBreakdown = null;

			if (taxCalc) {
				const totalTaxUSD = taxCalc.fees.reduce((sum, fee) => sum + fee.amount, 0);
				taxAmount = totalTaxUSD.toFixed(2);
				taxBreakdown = taxCalc.fees.map(fee => ({
					rate: fee.rate,
					type: fee.type,
					amount: Number((fee.amount / sechPrice).toFixed(8)),
					amountUSD: fee.amount,
					description: fee.description
				}));
			}

			let discountId = null;
			let discountCode = null;
			let discountAmount = null;
			let discountDetails = null;

			if (discount) {
				discountId = discount.discountId;
				discountCode = discount.promoCode;
				discountAmount = discount.calculatedAmount.toFixed(2);
				discountDetails = {
					discountId: discount.discountId,
					discountName: discount.discountName,
					discountType: discount.discountType,
					discountValue: discount.discountValue,
					appliedAmount: discount.calculatedAmount,
					eligibleSubtotal: discount.eligibleSubtotal,
				};
			}

			const dbOrder: Record<string, any> = {
				orderNumber: orderData?.orderNumber || undefined,
				locationId: orderData?.locationId,
				locationDetails: orderData?.locationDetails || null,
				locationName: orderData?.locationName,
				status: 'pending',
				notes: orderData?.notes || null,
				totalUsd: orderData.totalUsd,
				subtotal: subtotal,
				taxAmount: taxAmount,
				taxBreakdown: taxBreakdown,
				discountId: discountId,
				discountCode: discountCode,
				discountAmount: discountAmount,
				discountDetails: discountDetails,
				totalSech: orderData.totalSech.toString(),
				paymentHash: orderData.transactionHash,
				sechExchangeRate: sechPrice.toFixed(8),
				user: orderData?.user,
			};

			const dbOrderItems = items.map(item => ({
				productId: item.type === 'product' ? item.productId : null,
				customDrink: item.type === 'custom-drink' ? item.customDrink : null,
				quantity: item.quantity,
				unitPriceUsd: item.unitPriceUsd.toFixed(2),
				unitPriceSech: item.unitPriceSech.toString(),
				selectedModifiers: item.selectedModifiers || null
			}));

			const requestBody = {
				order: dbOrder,
				items: dbOrderItems,
				customerEmail: orderData.sechUser?.email || null
			};

			const response = await apiFetch('/v1/pos/orders', {
				withCredentials: true,
				method: 'POST',
				body: JSON.stringify(requestBody),
			});

			if (!response.ok) {
				const errorData = await response.text();
				throw new Error(`Failed to create order: ${errorData}`);
			}

			return await response.json();
		} catch (error: any) {
			const msg = error.message || "Order could not be placed. Please try again.";
			toast({
				title: "Order Failed",
				description: msg,
				variant: "destructive",
			});
			throw error;
		}
	};

	const requestPayment = async (orderId?: string, userData?: UserData, notes?: string, paymentMethod?: SelectablePaymentMethod) => {
		if (!sechUser) {
			toast({
				title: "SECH wallet required",
				description: "Please connect your SECH wallet to complete the purchase.",
				variant: "destructive",
			});
			return;
		}

		if (cartItems.length === 0) {
			toast({
				title: "Cart is empty",
				description: "Add items to cart before checkout",
				variant: "destructive",
			});
			return;
		}

		if (isPaymentProcessing || isCalculatingTaxes) return;

		if (!taxCalculation) {
			toast({
				title: "Tax calculation pending",
				description: "Please wait for tax calculation to complete",
				variant: "destructive",
			});
			return;
		}

		if (!sechPrice && (!paymentMethod || paymentMethod.type === 'TOKEN')) {
			toast({
				title: "Price unavailable",
				description: "Unable to get current SECH price",
				variant: "destructive",
			});
			return;
		}

		const totalWithTaxes = getCartTotalWithTaxes();
		const totalSech = totalWithTaxes / sechPrice;

		const taxBreakdown = taxCalculation.fees.map(fee => ({
			rate: fee.rate,
			type: fee.type,
			amount: Number((fee.amount / sechPrice).toFixed(8)),
			amountUSD: fee.amount,
			description: fee.description
		}));

		const discountedSubtotal = getDiscountedTotal();
		const discountedSubtotalSech = discountedSubtotal / sechPrice;
		const originalSubtotal = getCartTotal();
		const originalSubtotalSech = originalSubtotal / sechPrice;
		const taxAmountUsd = taxCalculation.fees.reduce((sum, fee) => sum + fee.amount, 0);
		const taxAmountSech = taxAmountUsd / sechPrice;

		userRef.current = userData || null;
		notesRef.current = notes || null;
		setIsPaymentProcessing(true);

		toast({
			title: "Processing payment...",
			description: paymentMethod?.type === 'CARD' ? "Processing credit card payment..." : "Please confirm the payment in your SECH wallet",
		});

		try {
			// Call the parent app's executePayment directly (no postMessage)
			const paymentResult = await executePayment({
				total: { sech: totalSech, usd: totalWithTaxes },
				subtotal: { sech: discountedSubtotalSech, usd: discountedSubtotal },
				originalSubtotal: { sech: originalSubtotalSech, usd: originalSubtotal },
				appliedDiscount: appliedDiscount || null,
				tax_amount: { sech: taxAmountSech, usd: taxAmountUsd },
				taxBreakdown,
				items: cartItems.map(item => ({
					id: item.id,
					name: item.product?.name || item.customDrink?.name,
					quantity: item.quantity,
					unitPriceUsd: item.unitPriceUsd,
					unitPriceSech: item.unitPriceSech
				})),
				location,
				user: userData,
				...(paymentMethod && { paymentMethodType: paymentMethod.type, ...(paymentMethod.type === 'CARD' && { paymentMethodId: paymentMethod.id }) })
			});

			if (paymentResult.success) {
				const currentItems = [...cartItems];
				const currentTaxCalc = taxCalculation;
				const currentDiscount = appliedDiscount;
				const currentTotal = currentTaxCalc ? currentTaxCalc.total : currentItems.reduce((sum, item) => sum + (item.unitPriceUsd * item.quantity), 0);
				const totalSechAmount = currentTotal / sechPrice;

				const txHash = paymentResult.txHash as any;
				const transactionHash: string | undefined = typeof txHash === 'object' && txHash !== null
					? (txHash.hash || String(txHash))
					: txHash;

				const orderData = {
					id: Date.now(),
					orderNumber: paymentResult.orderId || undefined,
					status: 'pending',
					locationId: location?.id,
					locationName: location?.name,
					locationDetails: location?.details || null,
					notes: notesRef.current || null,
					totalUsd: currentTotal.toFixed(2),
					totalSech: totalSechAmount,
					transactionHash,
					items: currentItems,
					taxCalculation: currentTaxCalc,
					discount: currentDiscount,
					createdAt: new Date().toISOString(),
					sechUser: {
						uid: sechUser?.uid,
						email: sechUser?.email,
						displayName: sechUser?.displayName,
						walletAddress: sechUser?.walletAddress
					},
					user: userRef.current
				};

				try {
					const savedOrder = await saveOrderToDatabase(orderData, currentItems, currentTaxCalc, currentDiscount, paymentMethod);

					const finalOrder = {
						...orderData,
						id: savedOrder.id,
						orderNumber: savedOrder.orderNumber
					};

					clearCart();

					toast({
						title: "Payment successful!",
						description: `Order ${finalOrder.orderNumber} has been placed successfully.`,
					});

					if (onPaymentSuccess) {
						onPaymentSuccess(finalOrder);
					}
				} catch (dbError: any) {
					let msg = "Order could not be saved. Please try again.";
					try {
						const parsed = JSON.parse(dbError.message.replace("Failed to create order:", "").trim());
						if (parsed.message) msg = parsed.message;
					} catch { /* ignore */ }

					toast({
						title: "Order Failed",
						description: msg || "Payment succeeded but there was a database error. Please contact support.",
						variant: "destructive",
					});
					setIsPaymentProcessing(false);
				}
			} else {
				toast({
					title: "Payment failed",
					description: paymentResult.error || "Payment could not be processed. Please try again.",
					variant: "destructive",
				});
				setIsPaymentProcessing(false);
			}
		} catch (err: any) {
			setIsPaymentProcessing(false);
			toast({
				title: "Payment failed",
				description: err.message || "Payment could not be processed. Please try again.",
				variant: "destructive",
			});
		}
	};

	const value: CartContextType = {
		cartItems,
		addToCart,
		removeFromCart,
		updateQuantity,
		clearCart,
		getCartTotal,
		getCartItemCount,
		sechUser,
		requestPayment,
		isPaymentProcessing,
		taxCalculation,
		isCalculatingTaxes,
		appliedDiscount,
		promoCode,
		setPromoCode,
		applyPromoCode,
		removeDiscount,
		isValidatingPromo,
		promoError,
		getDiscountedTotal,
		refreshDiscount,
	};

	return (
		<CartContext.Provider value={value}>
			{children}
			{pending && (
				<PosConfirmModal
					open={true}
					title="Replace items?"
					description={`Your cart has items from ${cartLocation}. You can order only from one location.`}
					confirmText="Replace"
					cancelText="Cancel"
					onClose={() => setPending(null)}
					variant={'logout'}
					onConfirm={() => {
						if (!pending) return;
						const newItem = pending;
						setPending(null);
						clearCart();
						setCartLocation(newItem.itemLocation);
						addDirect(newItem);
					}}
				/>
			)}
		</CartContext.Provider>
	);
}

export function usePosCart() {
	const context = useContext(CartContext);
	if (context === undefined) {
		throw new Error("usePosCart must be used within a PosCartProvider");
	}
	return context;
}
